import { useState, useEffect } from 'react';
import {
  settlementsApi,
  SettlementConfigResponse,
  TabbyFeeBearer,
  UpdateSettlementConfigRequest,
} from '../../services/settlementsApi';
import { useAuth } from '../../hooks/useAuth';

interface SettlementConfigFormProps {
  garageId: string;
}

const CYCLE_OPTIONS = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BI_WEEKLY', label: 'Bi-weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

/** Spec 067 — who pays the Tabby fee: shown as plain words, sent as GARAGE | GARAGAT. */
const BEARER_LABELS: Record<TabbyFeeBearer, string> = { GARAGE: 'Garage', GARAGAT: 'Garagat' };

function bearerLabel(value: string | null | undefined): string {
  return value === 'GARAGE' || value === 'GARAGAT' ? BEARER_LABELS[value] : '—';
}

export default function SettlementConfigForm({ garageId }: SettlementConfigFormProps) {
  const auth = useAuth();
  const [config, setConfig] = useState<SettlementConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [cycle, setCycle] = useState('BI_WEEKLY');
  const [commissionRate, setCommissionRate] = useState('20');
  const [settlementDay, setSettlementDay] = useState('1');
  // Spec 053 BR-15 — blank means "use the global default (3 days)".
  const [holdDays, setHoldDays] = useState('');
  // Spec 067 — blank means "use the global default" (Runtime Config TABBY_FEE_BEARER).
  const [tabbyFeeBearer, setTabbyFeeBearer] = useState<'' | TabbyFeeBearer>('');

  useEffect(() => {
    if (!auth.token) return;
    setLoading(true);
    settlementsApi.getGarageConfig(garageId, auth.token)
      .then((data) => {
        setConfig(data);
        setCycle(data.settlementCycle);
        setCommissionRate(String(data.commissionRate * 100));
        setSettlementDay(String(data.settlementDay));
        setHoldDays(data.holdDays == null ? '' : String(data.holdDays));
        setTabbyFeeBearer(data.tabbyFeeBearer ?? '');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token, garageId]);

  const handleSave = async () => {
    if (!auth.token) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const request: UpdateSettlementConfigRequest = {
        settlementCycle: cycle,
        commissionRate: parseFloat(commissionRate) / 100,
        settlementDay: parseInt(settlementDay, 10),
        holdDays: holdDays.trim() === '' ? null : parseInt(holdDays, 10),
        tabbyFeeBearer: tabbyFeeBearer === '' ? null : tabbyFeeBearer,
      };
      const updated = await settlementsApi.updateGarageConfig(garageId, request, auth.token);
      setConfig(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p style={{ color: 'var(--admin-text-muted)', fontSize: '0.875rem' }}>Loading settlement config...</p>;
  }

  const dayNum = parseInt(settlementDay, 10);
  const rateNum = parseFloat(commissionRate);
  const defaultHoldLabel = `Default (${config?.defaultHoldDays ?? 3} days)`;
  const holdBlank = holdDays.trim() === '';
  const holdNum = Number(holdDays);
  const holdValid = holdBlank || (Number.isInteger(holdNum) && holdNum >= 3 && holdNum <= 30);
  const isValid = !isNaN(dayNum) && dayNum >= 1 && dayNum <= 31
    && !isNaN(rateNum) && rateNum >= 0 && rateNum <= 100
    && holdValid;

  return (
    <div>
      {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {success && (
        <div className="admin-info-banner info" style={{ marginBottom: '0.75rem' }}>
          Settlement configuration updated successfully.
        </div>
      )}

      {config?.isDefault && (
        <div className="admin-info-banner info" style={{ marginBottom: '0.75rem' }}>
          Using default settlement terms. Save to create a custom configuration for this garage.
        </div>
      )}

      <div className="admin-detail-grid" style={{ gap: '1rem' }}>
        <div className="admin-field">
          <label className="admin-field-label" htmlFor="commission-rate">Commission Rate (%)</label>
          <input
            id="commission-rate"
            type="number"
            className="admin-input"
            value={commissionRate}
            onChange={(e) => setCommissionRate(e.target.value)}
            min="0"
            max="100"
            step="0.1"
          />
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
            Platform fee deducted from each order (applied at booking time)
          </span>
        </div>

        <div className="admin-field">
          <label className="admin-field-label" htmlFor="settlement-cycle">Settlement Cycle</label>
          <select
            id="settlement-cycle"
            className="admin-input"
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
          >
            {CYCLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="admin-field">
          <label className="admin-field-label" htmlFor="settlement-day">Settlement Day</label>
          <input
            id="settlement-day"
            type="number"
            className="admin-input"
            value={settlementDay}
            onChange={(e) => setSettlementDay(e.target.value)}
            min="1"
            max="31"
          />
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
            Day of the month/week when settlement period ends
          </span>
        </div>

        <div className="admin-field">
          <label className="admin-field-label" htmlFor="hold-days">Hold days</label>
          <input
            id="hold-days"
            type="number"
            className="admin-input"
            value={holdDays}
            onChange={(e) => setHoldDays(e.target.value)}
            placeholder={defaultHoldLabel}
            min="3"
            max="30"
            step="1"
            aria-invalid={!holdValid}
          />
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
            Days after completion before an order can be settled — {defaultHoldLabel} applies when left blank.
            Raise it for garages with refund problems.
          </span>
          {!holdValid && (
            <span style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
              Hold days must be between 3 and 30
            </span>
          )}
        </div>

        <div className="admin-field">
          <label className="admin-field-label" htmlFor="tabby-fee-bearer">Tabby fee paid by</label>
          <select
            id="tabby-fee-bearer"
            className="admin-input"
            value={tabbyFeeBearer}
            onChange={(e) => setTabbyFeeBearer(e.target.value as '' | TabbyFeeBearer)}
          >
            <option value="">Default ({bearerLabel(config?.defaultTabbyFeeBearer)})</option>
            <option value="GARAGE">Garage</option>
            <option value="GARAGAT">Garagat</option>
          </select>
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
            Applies now: {bearerLabel(config?.tabbyFeeBearerEffective)}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
            Applies to Tabby payments captured after you save.
          </span>
        </div>
      </div>

      {config && !config.isDefault && (
        <p style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.75rem' }}>
          Effective from: {new Date(config.effectiveFrom).toLocaleDateString()}
        </p>
      )}

      <div style={{ marginTop: '1rem' }}>
        <button
          className="admin-btn admin-btn-primary"
          disabled={saving || !isValid}
          onClick={handleSave}
        >
          {saving ? 'Saving...' : 'Save Settlement Terms'}
        </button>
      </div>
    </div>
  );
}
